/**
 * useTextSelection钩子
 * 处理文本选择相关逻辑
 */
import { useCallback } from 'react';
import { smartTruncate } from '@readsavor/utils';
/**
 * 文本选择处理钩子
 * 提取自OriginalTextView组件
 */
export function useTextSelection({ onTextSelect, paragraphMap }) {
    /**
     * 处理段落中的文本选择
     */
    const handleParagraphSelection = useCallback((e, eventTriggeredParagraph // 这是事件触发的段落 (可能是终点)
    ) => {
        if (!onTextSelect) {
            return;
        }
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            onTextSelect(null);
            return;
        }
        const selectedTextGlobal = selection.toString().trim();
        if (!selectedTextGlobal && selection.rangeCount > 0 && selection.getRangeAt(0).collapsed) {
            onTextSelect(null);
            return;
        }
        const selectionRange = selection.getRangeAt(0);
        const eventTargetElement = e.currentTarget;
        const rangeStartContainer = selectionRange.startContainer;
        let startParagraphElement = null;
        let currentTraverseNode = rangeStartContainer;
        while (currentTraverseNode) {
            if (currentTraverseNode.nodeType === Node.ELEMENT_NODE) {
                const element = currentTraverseNode;
                if (element.id && element.id.startsWith('paragraph-')) {
                    startParagraphElement = element;
                    break;
                }
            }
            if (currentTraverseNode === document.body || !currentTraverseNode.parentNode) {
                break;
            }
            currentTraverseNode = currentTraverseNode.parentNode;
        }
        if (!startParagraphElement) {
            startParagraphElement = eventTargetElement;
        }
        const startParagraphDomId = startParagraphElement.id;
        const startParagraphId = startParagraphDomId.replace('paragraph-', '');
        const initialStartParagraphDataFromMap = paragraphMap.get(startParagraphId);
        const currentParagraphForSelection = initialStartParagraphDataFromMap || eventTriggeredParagraph;
        let endOffsetInParagraph = 0;
        let textSelectedInParagraph = '';
        let finalCurrentParagraphData = currentParagraphForSelection;
        let finalStartParagraphElement = startParagraphElement;
        let finalStartOffsetInParagraph = 0;
        try {
            const initialTempStartRange = document.createRange();
            initialTempStartRange.setStart(finalStartParagraphElement, 0);
            initialTempStartRange.setEnd(selectionRange.startContainer, selectionRange.startOffset);
            finalStartOffsetInParagraph = initialTempStartRange.toString().length;
            const initialParagraphContent = finalCurrentParagraphData.content;
            if (finalStartOffsetInParagraph >= initialParagraphContent.length &&
                finalStartParagraphElement &&
                !finalStartParagraphElement.contains(selectionRange.endContainer)) {
                const currentIdStr = finalCurrentParagraphData.id;
                const match = currentIdStr.match(/^p-(\d+)$/);
                if (match && match[1]) {
                    const currentNumericId = parseInt(match[1], 10);
                    const nextNumericId = currentNumericId + 1;
                    const nextParagraphId = `p-${nextNumericId}`;
                    const nextParagraphData = paragraphMap.get(nextParagraphId);
                    if (nextParagraphData) {
                        finalCurrentParagraphData = nextParagraphData;
                        finalStartOffsetInParagraph = 0;
                        const nextParagraphDomElement = document.getElementById(`paragraph-${nextParagraphData.id}`);
                        if (nextParagraphDomElement) {
                            finalStartParagraphElement = nextParagraphDomElement;
                        }
                    }
                    else {
                        finalStartOffsetInParagraph = initialParagraphContent.length;
                    }
                }
                else {
                    finalStartOffsetInParagraph = initialParagraphContent.length;
                }
            }
            const paragraphContentToUse = finalCurrentParagraphData.content;
            if (finalStartOffsetInParagraph > paragraphContentToUse.length) {
                finalStartOffsetInParagraph = paragraphContentToUse.length;
            }
            if (finalStartParagraphElement &&
                finalStartParagraphElement.contains(selectionRange.endContainer)) {
                const tempEndRange = document.createRange();
                tempEndRange.setStart(finalStartParagraphElement, 0);
                tempEndRange.setEnd(selectionRange.endContainer, selectionRange.endOffset);
                endOffsetInParagraph = tempEndRange.toString().length;
            }
            else {
                endOffsetInParagraph = paragraphContentToUse.length;
            }
            if (endOffsetInParagraph > paragraphContentToUse.length) {
                endOffsetInParagraph = paragraphContentToUse.length;
            }
            if (finalStartOffsetInParagraph > endOffsetInParagraph) {
                endOffsetInParagraph = finalStartOffsetInParagraph;
            }
            textSelectedInParagraph = paragraphContentToUse.substring(finalStartOffsetInParagraph, endOffsetInParagraph);
        }
        catch (error) {
            textSelectedInParagraph = selectedTextGlobal;
            finalStartOffsetInParagraph = 0;
            const contentLen = finalCurrentParagraphData.content.length;
            endOffsetInParagraph =
                textSelectedInParagraph.length > contentLen ? contentLen : textSelectedInParagraph.length;
            textSelectedInParagraph = finalCurrentParagraphData.content.substring(finalStartOffsetInParagraph, endOffsetInParagraph);
        }
        if (!textSelectedInParagraph && selectedTextGlobal) {
            onTextSelect(null);
            return;
        }
        const MAX_SELECTION_LENGTH = 50;
        const truncatedText = smartTruncate(textSelectedInParagraph, MAX_SELECTION_LENGTH, MAX_SELECTION_LENGTH);
        const finalEndOffsetToUse = finalStartOffsetInParagraph + truncatedText.length;
        const finalSelectionData = {
            type: 'text',
            selectedText: truncatedText,
            location: {
                paragraphData: finalCurrentParagraphData,
                startOffset: finalStartOffsetInParagraph,
                endOffset: finalEndOffsetToUse,
            },
        };
        onTextSelect(finalSelectionData);
    }, [onTextSelect, paragraphMap]);
    /**
     * 处理普通文本的选择（非段落结构）
     */
    const handlePlainTextSelection = useCallback(() => {
        if (!onTextSelect) {
            return;
        }
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();
        if (selectedText) {
            // This case handles selection in a non-structured-paragraph context.
            // We can represent this by creating a synthetic paragraphData or
            // by adapting the SelectionData type. For now, we create a minimal
            // paragraph object.
            onTextSelect({
                type: 'text',
                selectedText: selectedText,
                location: {
                    paragraphData: {
                        id: 'plain-text-selection',
                        index: 0,
                        content: selectedText, // The content is just the selection itself
                    },
                    startOffset: 0,
                    endOffset: selectedText.length,
                },
            });
        }
    }, [onTextSelect]);
    return {
        handleParagraphSelection,
        handlePlainTextSelection,
    };
}
//# sourceMappingURL=useTextSelection.js.map